import { inject } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import type { Store } from "@inferno/renderer-shared-core";

import { Remote } from "../../../../../core/components/remote/Remote.component";
import {
  PWSMemberService,
  isPWSSessionBackend,
  PopupState,
  PWSSessionState,
  COAST_MESSAGES,
} from "@inferno/renderer-shared-core";
import { axiosLoaderWrapper } from "../../../lib/coastCommon";
import { getUserSettingsLoaderCacheKey } from "../../../lib/coastUser";
import { CoastUserSettings, UserSettingBillingData, UserSettingUserData } from "./CoastUserSettings.component";

interface CoastUserSettingsLoaderProps {
  viewData: PopupState["data"];
  store?: Store;
}

const userSettingsLoader = async (
  memberService: PWSMemberService,
  accessToken?: string | null,
): Promise<[UserSettingUserData, UserSettingBillingData]> =>
  accessToken
    ? Promise.all([
        axiosLoaderWrapper(() => memberService.getUser(accessToken), COAST_MESSAGES.ACCOUNT_FETCHING_ERROR),
        axiosLoaderWrapper(() => memberService.getBillingInfo(accessToken), COAST_MESSAGES.ACCOUNT_FETCHING_ERROR),
      ])
    : [{ data: null }, { data: null }];

export const CoastUserSettingsLoader = inject("store")(({ store }: CoastUserSettingsLoaderProps) => {
  const session = store?.session.currentSession;

  if (!store || !isPWSSessionBackend(session)) {
    return null;
  }

  const [accessToken, setAccessToken] = useState(session.state.accessToken);

  const handleSessionUpdate = useCallback((state: PWSSessionState) => setAccessToken(state.accessToken), []);

  useEffect(() => {
    session.onStatusChanged.subscribe(handleSessionUpdate);
    return () => session.onStatusChanged.unsubscribe(handleSessionUpdate);
  }, [handleSessionUpdate, session.onStatusChanged]);

  return (
    <Remote
      // WARNING: this is quite messy, but if the format of data returned by this loader gets update
      // it needs to be updated in the updateSubscription method CoastUserSettings too
      loader={() => userSettingsLoader(session.memberService, accessToken)}
      cacheKey={getUserSettingsLoaderCacheKey(accessToken)}
      showLoading={true}
    >
      {({ data }) => (data ? <CoastUserSettings userData={data[0]} billingData={data[1]} /> : null)}
    </Remote>
  );
});
